/**
 * useMilestoneAwarder — single helper to queue a claimable milestone reward.
 * Routes through the SECURITY DEFINER queue_pending_reward RPC, which is
 * idempotent so callers can fire-and-forget on every relevant action.
 */
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { logger } from "@/utils/logger";

export type MilestoneAction =
  | "create_first_goal"
  | "first_wishlist_item"
  | "first_wishlist_received"
  | "first_gift_sent"
  | "first_connection"
  | "generate_certificate"
  | "daily_streak_3"
  | "daily_streak_7"
  | "daily_streak_30"
  // Phase 1.2 — high-satisfaction actions now routed through Claim
  | "goal_complete"
  | "complete_common_goal";

export interface MilestoneSpec {
  action: MilestoneAction;
  points: number;
  description: string;
}

export const MILESTONES: Record<MilestoneAction, MilestoneSpec> = {
  create_first_goal:       { action: "create_first_goal",       points: 10, description: "You created your first goal" },
  first_wishlist_item:     { action: "first_wishlist_item",     points: 5,  description: "You added your first gift list item" },
  first_wishlist_received: { action: "first_wishlist_received", points: 10, description: "Someone reserved a gift for you" },
  first_gift_sent:         { action: "first_gift_sent",         points: 5,  description: "You pledged your first reward" },
  first_connection:        { action: "first_connection",        points: 5,  description: "You made your first connection" },
  generate_certificate:    { action: "generate_certificate",    points: 15, description: "You generated your first certificate" },
  daily_streak_3:          { action: "daily_streak_3",          points: 10, description: "3-day streak" },
  daily_streak_7:          { action: "daily_streak_7",          points: 25, description: "7-day streak" },
  daily_streak_30:         { action: "daily_streak_30",         points: 100, description: "30-day streak" },
  goal_complete:           { action: "goal_complete",           points: 30, description: "You completed a goal" },
  complete_common_goal:    { action: "complete_common_goal",    points: 20, description: "You completed a community goal" },
};

export function useMilestoneAwarder() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useCallback(
    async (
      action: MilestoneAction,
      referenceId?: string | null,
      descriptionOverride?: string,
    ) => {
      if (!user?.id) return { success: false, error: "no_user" };
      const spec = MILESTONES[action];
      try {
        const { data, error } = await supabase.rpc("queue_pending_reward", {
          p_action: spec.action,
          p_points: spec.points,
          p_reference_id: referenceId ?? null,
          p_description: descriptionOverride ?? spec.description,
        });
        if (error) {
          logger.warn("queue_pending_reward error", error);
          return { success: false, error: error.message };
        }
        const result = data as { success: boolean; error?: string; id?: string };
        if (result?.success) {
          queryClient.invalidateQueries({ queryKey: ["pendingRewards", user.id] });
          // Notify popup provider so it can fire the game-style claim popup.
          try {
            window.dispatchEvent(
              new CustomEvent("reward-queued", {
                detail: { action: spec.action, points: spec.points, id: result.id },
              }),
            );
          } catch {
            // no-op
          }
        }
        return result;
      } catch (e) {
        logger.warn("queue_pending_reward exception", e);
        return { success: false, error: "exception" };
      }
    },
    [user?.id, queryClient]
  );
}
